// components/features/analysis/IconSection.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconContainer,
  IconImage,
  BookmarkButton,
  RescanButton,
  DownloadButton,
  ReportButton,
  InvestigateButton,
  BookmarkIcon,
  RefreshIcon,
  DownloadIcon,
  ReportIcon,
  InvestigateIcon,
  ButtonsContainer
} from '../../../styles/AnalysisStyles';

const IconSection = ({ 
  analysisData, 
  isBookmarked, 
  toggleBookmark, 
  canRescan, 
  handleRescan, 
  canDownload, 
  isDownloading, 
  handleDownload 
}) => {
  const [iconData, setIconData] = useState('');
  const [isRescanning, setIsRescanning] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const navigate = useNavigate();

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    if (analysisData?.icon) {
      // Convert base64 icon data to display format
      setIconData(`data:image/png;base64,${analysisData.icon}`);
    }
  }, [analysisData]);

  const onRescanClick = async () => {
    if (!canRescan || isRescanning) return;
    
    setIsRescanning(true);
    try {
      const success = await handleRescan();
      
      if (success) {
        setMessage({ 
          text: 'Rescan initiated successfully.', 
          type: 'success' 
        });
        
        // Start polling for completion
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await fetch(`/api/files/analysis/${analysisData.sha256}/status`, {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (!statusResponse.ok) {
              throw new Error(`Server responded with status: ${statusResponse.status}`);
            }
            
            const statusData = await statusResponse.json();
            
            if (statusData.analysis_status === 'completed') {
              clearInterval(pollInterval);
              setIsRescanning(false);
              // Refresh the page to show new analysis results
              navigate(0); // This will refresh the current page
            }
          } catch (error) {
            console.error('Error polling analysis status:', error);
          }
        }, 5000); // Poll every 5 seconds
        
        // Set a timeout to stop polling after a reasonable time (5 minutes)
        setTimeout(() => {
          clearInterval(pollInterval);
          setIsRescanning(false);
          setMessage({
            text: 'Rescan is taking longer than expected. You may need to refresh the page manually.',
            type: 'info'
          });
        }, 300000);
      } else {
        throw new Error('Rescan failed');
      }
    } catch (error) {
      console.error('Error initiating rescan:', error);
      setMessage({
        text: 'Failed to start rescan. Please try again later.',
        type: 'error'
      });
      setIsRescanning(false);
    }
  };

  const onReportDownload = () => {
    const reportData = { ...analysisData };
    delete reportData._id;
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${analysisData.sha256}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setMessage({ text: 'Report downloaded.', type: 'success' });
  };

  const onDownloadClick = async () => {
    if (!canDownload || isDownloading) return;
    
    try {
      await handleDownload();
      setMessage({ 
        text: 'Download started successfully!', 
        type: 'success' 
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      setMessage({
        text: 'Failed to download file. Please try again later.',
        type: 'error'
      });
    }
  };

  // Message Component (keeping the inline styles as they're unique to this component)
  const MessageBox = ({ message }) => {
    if (!message.text) return null;
    
    const getMessageStyles = () => ({
      padding: '8px 12px',
      marginBottom: '10px',
      borderRadius: '4px',
      fontSize: '14px',
      maxWidth: '200px',
      textAlign: 'center',
      backgroundColor: message.type === 'success' ? '#e6fffa' : 
                      message.type === 'error' ? '#fff5f5' : '#ebf8ff',
      color: message.type === 'success' ? '#2c7a7b' : 
            message.type === 'error' ? '#c53030' : '#2b6cb0',
      border: message.type === 'success' ? '1px solid #81e6d9' : 
            message.type === 'error' ? '1px solid #feb2b2' : '1px solid #bee3f8'
    });

    return (
      <div style={getMessageStyles()}>
        {message.text}
      </div>
    );
  };

  return (
    <IconContainer>
      {iconData && <IconImage src={iconData} alt="App Icon" />}
      
      <MessageBox message={message} />
      
      <ButtonsContainer>
        <BookmarkButton 
          isBookmarked={isBookmarked} 
          onClick={toggleBookmark}
          title={isBookmarked ? "Remove bookmark" : "Add bookmark"}
        >
          <BookmarkIcon isBookmarked={isBookmarked} />
        </BookmarkButton>
        
        <RescanButton 
          disabled={!canRescan || isRescanning}
          onClick={onRescanClick}
          title={canRescan ? "Rescan APK file" : "APK file not available for rescan"}
        >
          <RefreshIcon isSpinning={isRescanning} />
        </RescanButton>

        <DownloadButton
          disabled={!canDownload || isDownloading}
          onClick={onDownloadClick}
          title={
            isDownloading
              ? "Downloading APK file..."
              : canDownload
                ? "Download APK file"
                : "APK file not available for download"
          }
        >
          <DownloadIcon isDownloading={isDownloading} />
        </DownloadButton>

        <ReportButton
          onClick={onReportDownload}
          title="Download JSON report"
        >
          <ReportIcon />
        </ReportButton>

        <InvestigateButton
          onClick={() => navigate(`/investigate?sha256=${analysisData.sha256}`)}
          title="Investigate in graph view"
        >
          <InvestigateIcon />
        </InvestigateButton>
      </ButtonsContainer>
    </IconContainer>
  );
};

export default IconSection;